import { circle as turfCircle } from "@turf/circle";
import { distance as turfDistance } from "@turf/distance";
import { point as turfPoint } from "@turf/helpers";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
const Constants = MapboxDraw.modes;

const doubleClickZoom = {
  enable: (ctx) => {
    setTimeout(() => {
      if (
        !ctx.map ||
        !ctx.map.doubleClickZoom ||
        !ctx._ctx?.store?.getInitialConfigValue
      )
        return;
      if (!ctx._ctx.store.getInitialConfigValue("doubleClickZoom")) return;
      ctx.map.doubleClickZoom.enable();
    }, 0);
  },
  disable(ctx) {
    setTimeout(() => {
      if (!ctx.map || !ctx.map.doubleClickZoom) return;
      ctx.map.doubleClickZoom.disable();
    }, 0);
  },
};

const DragCircleMode = {
  onSetup: function () {
    const polygon = this.newFeature({
      type: "Feature",
      properties: {
        isCircle: true,
        center: [],
        radiusInKm: 0,
      },
      geometry: {
        type: "Polygon",
        coordinates: [[]],
      },
    });

    this.addFeature(polygon);
    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);
    this.updateUIClasses({ mouse: "pointer" });
    this.activateUIButton();
    this.setActionableState({ trash: true });

    return { polygon, dragging: false, settingCenter: true };
  },

  // Extract coordinates from either mouse or touch event
  getEventCoords: function (e) {
    if (e.lngLat) {
      return [e.lngLat.lng, e.lngLat.lat];
    } else if (e.touches && e.touches.length > 0 && e.touches[0].lngLat) {
      return [e.touches[0].lngLat.lng, e.touches[0].lngLat.lat];
    }
    return null;
  },

  onMouseMove: function (state, e) {
    const coords = this.getEventCoords(e);
    if (coords) {
      this.handleDragMove(state, coords[0], coords[1]);
    }
  },

  onTouchMove: function (state, e) {
    // Prevent default scrolling behavior on touch move
    e.preventDefault();

    const coords = this.getEventCoords(e);
    if (coords) {
      this.handleDragMove(state, coords[0], coords[1]);
    }
  },

  handleDragMove: function (state, lng, lat) {
    if (state.dragging) {
      // Move the center (drag operation)
      state.polygon.properties.center = [lng, lat];
      const distanceInKm = state.polygon.properties.radiusInKm;
      const circleFeature = turfCircle(
        state.polygon.properties.center,
        distanceInKm,
        { steps: 64 }
      );
      state.polygon.setCoordinates(circleFeature.geometry.coordinates);
      return;
    }
    if (state.settingCenter) return;

    // Adjust radius
    const center = state.polygon.properties.center;
    if (!center || center.length === 0) return;

    const distanceInKm = turfDistance(
      turfPoint(center),
      turfPoint([lng, lat]),
      { units: "kilometers" }
    );
    const circleFeature = turfCircle(center, distanceInKm, { steps: 64 });
    state.polygon.setCoordinates(circleFeature.geometry.coordinates);
    state.polygon.properties.radiusInKm = distanceInKm;
  },

  onClick: function (state, e) {
    const coords = this.getEventCoords(e);
    if (!coords) return;

    if (state.settingCenter) {
      state.polygon.properties.center = coords;
      state.settingCenter = false;
    } else {
      return this.changeMode("simple_select", {
        featureIds: [state.polygon.id],
      });
    }
  },

  onTap: function (state, e) {
    // Ensure tap events work the same as click events
    return this.onClick(state, e);
  },

  onTouchStart: function (state, e) {
    e.preventDefault();
    const coords = this.getEventCoords(e);
    if (coords) {
      this.handleDragStart(state, coords[0], coords[1]);
    }
  },

  onMouseDown: function (state, e) {
    const coords = this.getEventCoords(e);
    if (coords) {
      this.handleDragStart(state, coords[0], coords[1]);
    }
  },

  handleDragStart: function (state, lng, lat) {
    const center = state.polygon.properties.center;
    if (center && center.length > 0) {
      const distance = turfDistance(turfPoint(center), turfPoint([lng, lat]), {
        units: "kilometers",
      });
      // If the point is inside the circle, allow dragging
      if (distance < state.polygon.properties.radiusInKm) {
        state.dragging = true;
      }
    }
  },

  onMouseUp: function (state) {
    this.handleDragEnd(state);
  },

  onTouchEnd: function (state) {
    this.handleDragEnd(state);
  },

  handleDragEnd: function (state) {
    state.dragging = false;
  },

  onStop: function (state) {
    doubleClickZoom.enable(this);
    this.updateUIClasses({ mouse: "none" });
    this.activateUIButton();

    if (!this.getFeature(state.polygon.id)) return;

    if (state.polygon.isValid()) {
      this.map.fire("draw.create", { features: [state.polygon.toGeoJSON()] });
    } else {
      this.deleteFeature([state.polygon.id], { silent: true });
      this.changeMode("simple_select", {}, { silent: true });
    }
  },

  toDisplayFeatures: function (state, geojson, display) {
    geojson.properties.active =
      geojson.properties.id === state.polygon.id ? "true" : "false";
    display(geojson);
  },
};

export default DragCircleMode;
